<template>
  <ChartCard
    v-if="!pageLoading"
    :data-widget-id="id"
  >
    <ChartHeader
      :label="itemLabel"
      class="mb-2 applicant-header"
    >
      <template #rightSide>
        <RequisitionReportWidgetCommentsToggle
          class="mr-3"
          :widget-id="id"
          :req-id="reqId"
          :req-uuid="reqUuid"
          :screenshot-selector="`[data-widget-id='${id}']`"
          :thread-name="title"
        />
        <ChartDownload
          :download-id="id"
          :file-name="`${projectName}-requisition-report-${id}-${today}`"
        />
        <InsightsTooltip :tooltip="tooltip" />
      </template>
    </ChartHeader>
    <div class="applicants-widget-content">
      <div class="metric-container left-container">
        <h5 class="previous-week-value">
          {{ previousUnit }}
        </h5>
        <span class="metric-label mt-1">Previous {{ unitDays }} Days</span>
      </div>
      <div
        class="icon-container mb-9"
      >
        <img
          v-if="lastUnit > previousUnit"
          src="@/assets/grey-arrow-up.png"
          alt="arrow up"
        >
        <img
          v-else-if="lastUnit < previousUnit"
          src="@/assets/grey-arrow-down.png"
          alt="arrow down"
        >
        <img
          v-else
          src="@/assets/grey-arrow-right.png"
          alt="arrow right"
        >
      </div>
      <div class="metric-container right-container">
        <h5 class="last-week-value">
          {{ lastUnit }}
        </h5>
        <span class="metric-label mt-1">Last {{ unitDays }} Days</span>
      </div>
    </div>
    <UnitButtons :selected-unit="applicantWidgetUnit" @update="handleUnitButtonUpdate" />
  </ChartCard>
  <RequisitionReportApplicantsSkeleton
    v-else
    class="loader"
  />
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import RequisitionReportApplicantsSkeleton from '@/components/requisitions/skeletonLoaders/RequisitionReportApplicantsSkeleton.vue';
import { useTooltips } from '@/stores/insights/tooltips';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import UnitButtons from '@/components/requisitions/RequisitionReportApplicantsWidget/UnitButtons.vue';
import { getPreviousDays, READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import { format } from 'date-fns';
import { startCase } from 'lodash-es';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import RequisitionReportWidgetCommentsToggle from '../RequisitionReportWidgetCommentsToggle.vue';

export default {
  name: 'RequisitionReportApplicantsWidget',
  components: {
    RequisitionReportWidgetCommentsToggle,
    RequisitionReportApplicantsSkeleton,
    InsightsTooltip,
    UnitButtons,
    ChartDownload,
    ChartHeader,
    ChartCard,
  },
  props: {
    projectName: { type: String, default: '' },
  },
  data() {
    return {
      id: 'requisition-applicants',
      tooltipKey: 'single-req-apps',
      itemLabel: 'How many applicants have we received recently?',
      keys: {
        week: {
          last: 'lastWeek',
          previous: 'previousWeek',
        },
        month: {
          last: 'lastMonth',
          previous: 'previousMonth',
        },
        quarter: {
          last: 'lastQuarter',
          previous: 'previousQuarter',
        },
      },
      days: {
        week: 7,
        month: 30,
        quarter: 90,
      },
      today: format(new Date(), READABLE_FILE_NAME_FORMAT),
    };
  },
  computed: {
    ...mapState(useTooltips, ['tooltips']),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useRequisitionReport, ['applicants', 'reqId', 'reqUuid', 'title', 'applicantWidgetUnit']),
    lastUnitKey() {
      return this.keys[this.applicantWidgetUnit]?.last || 'lastWeek';
    },
    previousUnitKey() {
      return this.keys[this.applicantWidgetUnit]?.previous || 'previousWeek';
    },
    previousUnit() {
      return this.applicants?.[this.previousUnitKey] || 0;
    },
    lastUnit() {
      return this.applicants?.[this.lastUnitKey] || 0;
    },
    unitDays() {
      return this.days[this.applicantWidgetUnit] || 7;
    },
    tooltip() {
      const { body_r: html, mode } = this.tooltips?.[this.tooltipKey] || {};

      const offsets = {
        week: {
          lastStartOffset: 6,
          previoiusStartOffset: 13,
          previousEndOffset: 7,
        },
        month: {
          lastStartOffset: 29,
          previoiusStartOffset: 59,
          previousEndOffset: 30,
        },
        quarter: {
          lastStartOffset: 89,
          previoiusStartOffset: 179,
          previousEndOffset: 90,
        },
      };

      const lastRange = getPreviousDays({
        rangeStartOffset: offsets[this.applicantWidgetUnit].lastStartOffset,
        rangeEndOffset: 0,
      }).map((date) => format(date, 'MM/dd/yyyy'));

      const previousRange = getPreviousDays({
        rangeStartOffset: offsets[this.applicantWidgetUnit].previoiusStartOffset,
        rangeEndOffset: offsets[this.applicantWidgetUnit].previousEndOffset,
      }).map((date) => format(date, 'MM/dd/yyyy'));

      let newHtml = '';

      const label = startCase(this.applicantWidgetUnit);

      const rangeText = `
        <h5 class="pb-1">Currently showing:</h5>
        <strong>Last ${label}: </strong>\n<span>${lastRange[0]} - ${lastRange[1]}</span>
        <strong>Previous ${label}: \n</strong><span>${previousRange[0]} - ${previousRange[1]}</span>`;

      if (!html) newHtml = rangeText;
      else newHtml = `${html}\n${rangeText}`;

      return {
        html: newHtml,
        mode,
        key: this.tooltipKey,
      };
    },
  },
  beforeUnmount() {
    this.updateApplicantWidgetUnit('week');
  },
  methods: {
    ...mapActions(useRequisitionReport, [
      'updateApplicantWidgetUnit',
    ]),
    handleUnitButtonUpdate(unit) {
      this.updateApplicantWidgetUnit(unit);
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.loader {
  height: 315px;
  border-radius: 10px;
  flex: 1;
  .shadow();
}

.icon-container {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
  }
}

.applicants-widget-content {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  margin-top: 36px;
}

.last-week-value {
  font-size: 36px;
  line-height: 48px;
  font-weight: 700;
  color: @text-brand;
}

.previous-week-value {
  font-size: 36px;
  line-height: 48px;
  font-weight: 700;
  color: @text-darkest;
}

.metric-label {
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
  color: @text-darkest;
}

.metric-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.right-container {
  margin-left: 41px;

  .last-week-value {
    font-size: 60px;
    line-height: normal;
  }
}

.applicant-header :deep(h2) {
  max-width: 350px;
}
</style>
